import React, { memo, useCallback } from "react";
import { StarIcon, StarOutlineIcon } from "modules/icons";
import colors from "app/colors";
import {
  EditMeMutation,
  EditMeMutationVariables,
  UserFragment,
} from "gql/graphql";
import { useAuth } from "modules/providers/auth-provider";
import { useMutation } from "@apollo/client";
import EDIT_ME_MUTATION from "modules/hooks/_networking/edit-me";
import CircularIconButton from "modules/_shared/buttons/circular-icon-button";
import SyncoTooltip from "modules/_shared/synco-tooltip";

interface IProps {
  user: UserFragment;
}

const UserToggleStarredButton = ({ user }: IProps) => {
  const { me } = useAuth();

  const [editMeMutationAPI] = useMutation<
    EditMeMutation,
    EditMeMutationVariables
  >(EDIT_ME_MUTATION);

  const onClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      if (!me) return;
      e.stopPropagation();
      e.preventDefault();
      const starredIds = me.starredIdList ?? [];
      const newStarredIdList = starredIds.includes(user.id)
        ? starredIds.filter((id) => id !== user.id)
        : [...starredIds, user.id];
      editMeMutationAPI({
        variables: {
          input: {
            starredIdList: newStarredIdList,
          },
        },
        optimisticResponse: {
          editMe: {
            ...me,
            starredIdList: newStarredIdList,
          },
        },
      });
    },
    [me, editMeMutationAPI, user.id],
  );

  if (!me || me.id === user.id) return null;

  const isStarred = me.starredIdList?.includes(user.id);

  return (
    <SyncoTooltip placement="top" label={isStarred ? "Unstar" : "Star"}>
      <CircularIconButton
        size="xs"
        aria-label="Starred"
        icon={
          isStarred ? (
            <StarIcon boxSize={6} fill={colors.yellowDark} />
          ) : (
            <StarOutlineIcon
              boxSize={6}
              stroke={colors.gray40}
              fill={colors.white}
            />
          )
        }
        onClick={onClick}
      />
    </SyncoTooltip>
  );
};

export default memo(UserToggleStarredButton);
